/**
 * The ChoreStory Next application shell (#23 / R5.1).
 *
 * HashRouter, deliberately. The 80 recovered modules carry `#/…` link defaults, a deep route
 * survives a direct refresh on a plain static host with no rewrite rule, and the marketing site
 * keeps its own separate Astro release artifact (F-31).
 *
 * The shell owns route ownership, the four route boundaries and the two providers. It owns no
 * screen content: every surface is a recovered page module.
 */
import React, { Suspense, useEffect } from 'react';
import { HashRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import FixtureBanner from './routes/FixtureBanner.jsx';
import LanternLoader from './components/LanternLoader.jsx';
import LoginRoute from './routes/LoginRoute.jsx';
import OnboardingRoute from './routes/OnboardingRoute.jsx';
import ApprovalsRoute from './routes/ApprovalsRoute.jsx';
import DashboardRoute from './routes/DashboardRoute.jsx';
import TodayRoute from './routes/TodayRoute.jsx';
import VillageRoute from './routes/VillageRoute.jsx';
import SignupRoute from './routes/SignupRoute.jsx';
import SettingsRoute from './routes/SettingsRoute.jsx';
import AuthRoute from './routes/AuthRoute.jsx';
import VerifyEmailRoute from './routes/VerifyEmailRoute.jsx';
import PairRoute, { PAIR_PATH } from './routes/PairRoute.jsx';
import DisplayRoute from './routes/DisplayRoute.jsx';
import FamilyRoute from './routes/FamilyRoute.jsx';
import QuestStudioRoute from './routes/QuestStudioRoute.jsx';
import LegalRoute from './routes/LegalRoute.jsx';
import WalletRoute from './routes/WalletRoute.jsx';
import InsightsRoute from './routes/InsightsRoute.jsx';
import TrophiesRoute from './routes/TrophiesRoute.jsx';
import { SnapshotNoticeVisibility } from './components/SnapshotNotice.jsx';
import { fetchSession } from './api/session.js';
import { fetchSoundPreferences, saveSoundPreferencesFromProvider } from './api/soundPreferences.js';
import { AUDIO_ASSETS } from './lib/audioAssets.js';
import { pageFixtures } from './fixtures/pageFixtures.js';
import FixtureGallery from './routes/FixtureGallery.jsx';
import RouteErrorBoundary from './routes/RouteErrorBoundary.jsx';
import RouteNotBuilt from './routes/RouteNotBuilt.jsx';
import { enterFixtureMode, exitFixtureMode } from './routes/fixtureMode.js';
import { DEFAULT_ROUTE, GALLERY_SURFACES, ROUTES, ROUTE_ALIASES, UNBUILT_ROUTES } from './routes/routeTable.js';
import { ActorProvider } from './state/ActorContext.jsx';
import ChildActorReturn from './state/ChildActorReturn.jsx';
import { HouseholdRealtimeProvider } from './state/HouseholdRealtime.jsx';
import { ParentRecheckProvider } from './state/ParentRecheck.jsx';
import { ThemeController } from './state/ThemeController.jsx';
import { themeStore } from './state/themeStore.js';

/**
 * Registered paths that talk to the real server, keyed by path (#26 / R6.1).
 *
 * `routeTable.js` owns which surfaces EXIST. This map owns which of them are wired to the backend
 * yet, which is a different question and moves on its own schedule as R6 and R7 land. A path
 * listed here renders its container instead of the fixture-backed surface, and its container is
 * responsible for supplying the recovered page's props from live data.
 */
const LIVE_CONTAINERS = {
  '/login': LoginRoute,
  '/signup': SignupRoute,
  '/onboarding': OnboardingRoute,
  // #30 (R7.2). The first surface past the front door to read live family data, and the first
  // consumer of F-24's `GET /api/payday/preview` and `POST /api/payday/settle`, which shipped
  // with no screen calling either.
  '/approvals': ApprovalsRoute,
  // #30 (R7.2). The family's shared goal, and the first consumer of R7.1's
  // `GET /api/village`. The card it renders ships four defaults that state facts about a
  // family it has never met — a week, a lantern count, a Festival Stage, a Monday — and
  // the container overrides every one of them. See `lib/villageMapping.js`.
  '/village': VillageRoute,
  // #30 (R7.2). The parent home screen. Four of its six tiles have no contract in
  // `GET /api/dashboard-data`, and its completion list spans NINETY SECONDS behind a server
  // variable named `allCompletedToday` (`dashboard.service.js:109`). See `lib/dashboardMapping.js`.
  '/dashboard': DashboardRoute,
  // #30 (R7.2). Two reads, because `GET /api/dashboard-data` never says which row is the
  // caller. The WRITE half is wired: a tap saves, and the screen's choice between "Awaiting
  // approval" and "Completion saved" comes from the server, because it is a claim about money
  // a parent may still hold. See `routes/TodayRoute.jsx`.
  '/today': TodayRoute,
  // #27 (R6.2). The only screen that can save a theme, and it could not while it was a preview:
  // `Surface` below calls `enterFixtureMode()` for every path missing from THIS map, and that flag
  // makes `persistWithReceipt` return `null` before a request is built. Wiring `themeStore` alone
  // would have changed nothing. See `routes/SettingsRoute.jsx`.
  '/settings': SettingsRoute,
  // #577 (R6.2). The parent admin screen. Three reads back it — the member list, the display
  // tokens and one goal-progress call per child — and three things it draws have no endpoint at
  // all: the pending-invitation list, a shareable join link, and a pairing code (creating one is
  // a POST, and rendering a page must not mutate a household). See `routes/FamilyRoute.jsx`.
  '/family': FamilyRoute,
  // #577 (R6.2). Where a parent makes a quest. Three reads back it, and the WRITE is the reason
  // this route waited: `handleSave` sends a weekday set, an XP value and an approval rule, and
  // until `schedule_days` landed the first had no column and the other two were dropped by the
  // write schema. See `routes/QuestStudioRoute.jsx`.
  '/quest-studio': QuestStudioRoute,
  // #576 L25. `/legal` was routed but had no entry here, so the family's actual Privacy Policy
  // rendered inside the fixture gallery — measured on dev `7e25d6c` with the gallery's "Nothing
  // here can be saved" notice across the top of it. This container fetches nothing: `Legal.jsx`
  // reads `shared/legal-source.json` directly, which is the single versioned source F-07 left in
  // place on purpose. Being outside fixture mode IS the change.
  '/legal': LegalRoute,
  // #579 (AD-07). A child's own money. Two reads, because `GET /api/wallet/:userId` needs an id in
  // its path and nothing the browser holds names the caller, so verify goes first — the same
  // reason `/today` above reads it. Settled, approved-but-unpaid and awaiting-review are THREE
  // fields on the payload and stay three sentences on the screen; the hero carries the settled
  // total alone. It reads no payday window and calls no `/api/payday/preview`, which is
  // `requireAdmin` and would 403 a child. See `routes/WalletRoute.jsx` and `lib/walletMapping.js`.
  '/wallet': WalletRoute,
  // #583/#584 N1 S12 (D6, D7). The parent's family report: ONE stored snapshot built through
  // `POST /api/insights/snapshots`, drawn as chart, table, drill-in (its stored facts) and CSV (its export),
  // plus the D-60 family activity list with the Q4 approved-XP measure. A child or a device is refused by the
  // server and the screen says so with no numbers. See `routes/InsightsRoute.jsx` and `lib/insightsMapping.js`.
  '/insights': InsightsRoute,
  // #583/#584 N1 S7. One member's badges, goal candles and 28-day streak from `GET /api/trophies/:userId`,
  // and — while that story switch is off — the badges, goals and streak record the older reads already
  // serve, labelled as such. A child reads only their own; a parent may choose. See `routes/TrophiesRoute.jsx`.
  '/trophies': TrophiesRoute,
  // #582 slice 5 (design C1). The shared kitchen display's live board. It reads as a DEVICE, never
  // as a person: every member's lanterns from the device projection of `GET /api/dashboard-data`,
  // a 600 ms hold plus that member's own PIN to light one, and nothing at all — no sample family —
  // until the server answers verify with `kind: 'device'`, `contractVersion: 2`. `PairRoute` sends a
  // display here only after the server confirms it. See `routes/DisplayRoute.jsx`.
  '/display': DisplayRoute,
};

function RouteFallback() {
  return (
    <div style={{ padding: '2rem' }}>
      <LanternLoader label="Waiting for this page; its content is not available yet." />
    </div>
  );
}

function UnknownRoute() {
  const location = useLocation();
  return (
    <main data-route-state="unknown" style={{ padding: '2rem', maxWidth: '42rem' }}>
      <h1>That page does not exist</h1>
      <p>
        No surface is registered at <code>#{location.pathname}</code>.
      </p>
      <p>
        <a href="#/today">Back to Today</a>
      </p>
    </main>
  );
}

function Surface({ surface, name, element: Element }) {
  // A surface with no live container is fixture-backed. The flag is set while the surface is
  // mounted and cleared when it unmounts, which is what makes the API client's mutation guard
  // reliable rather than advisory. #26 wired the first live route; the rest follow it.
  useEffect(() => {
    enterFixtureMode();
    return exitFixtureMode;
  }, []);

  return (
    <RouteErrorBoundary surface={surface}>
      <FixtureBanner />
      <SnapshotNoticeVisibility.Provider value={false}>
        <Suspense fallback={<RouteFallback />}>
          <Element {...pageFixtures[name]} />
        </Suspense>
      </SnapshotNoticeVisibility.Provider>
    </RouteErrorBoundary>
  );
}

/**
 * `#/gallery/<name>` — the fixture preview of ONE surface, by name rather than by path (#25 / R5.3).
 *
 * This exists because the gallery's promise and the live routes collided. A gallery card used to
 * link to `route.path`, and once that path gained a `LIVE_CONTAINERS` entry the card stopped
 * previewing anything: the container issued its GET and rendered server state, or an empty state,
 * under a page that says every screen below renders from fixture data. Measured on the deployed
 * preview 2026-09-11 — eight of the eighteen surfaces were in that state.
 *
 * Keying on the NAME rather than the path is what makes this stable. A surface keeps its name when
 * it goes live, so a card never needs changing again as R6 and R7 wire more containers.
 *
 * It renders the same `Surface` the fixture routes use, so it inherits the fixture contract whole:
 * the persistent preview notice, the mutation guard, and the recovered page's own fixture props.
 */
function GallerySurface() {
  const { name } = useParams();
  const route = GALLERY_SURFACES.find((r) => r.name === name);

  if (!route) {
    return (
      <main style={{ padding: '2rem 1.5rem' }} data-route-state="gallery-unknown">
        <h1>There is no surface named “{name}”.</h1>
        <p>
          The preview gallery lists every surface that exists. This link does not name one of them,
          so nothing is shown rather than something being invented.
        </p>
      </main>
    );
  }

  return <Surface surface={route.surface} name={route.name} element={route.element} />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to={DEFAULT_ROUTE} replace />} />
      <Route path="/gallery" element={<FixtureGallery />} />
      <Route path="/gallery/:name" element={<GallerySurface />} />
      {/*
        The verification link's landing (#26 / R6.1), registered HERE rather than in `ROUTES`.

        `ROUTES` holds 17 recovered product surfaces plus the auth wildcard.
        `GALLERY_SURFACES` holds all 18 recovered previews, including the preview-only operator
        mockup; the gallery index and D-10 fixture-identity sweep assert that preview count.
        This page was never part of the recovered design, because the verification email
        used to aim at the legacy app.

        `/gallery` above is the same shape, and it is the established way to register a surface
        the recovery did not produce.
      */}
      {/* #576 L26 — the auth utility, live.
        *
        * `routeTable.js:62-63` already routes `/auth` and `/auth/*` into the fixture gallery, so
        * without these three the forgot, reset and invitation screens render a preview that can
        * do nothing. They win over that wildcard on SPECIFICITY — React Router v6 ranks routes
        * rather than taking the first match — so their position here is for readability, not
        * correctness. `authUtilityRoutes.test.jsx` pins the outcome either way.
        *
        * `/verify-email` keeps its own container directly below, and `AuthRoute` deliberately
        * does not claim it: that link is single-use and spends its token on arrival. */}
      <Route path="/auth/forgot-password" element={<AuthRoute />} />
      <Route path="/auth/reset-password" element={<AuthRoute />} />
      <Route path="/auth/setup-account" element={<AuthRoute />} />
      <Route path="/verify-email" element={<VerifyEmailRoute />} />
      {/* #582 — where a display types the pairing code a parent made. Outside `ROUTES` for the
        * same reason as `/verify-email`: it is not one of the 18 recovered surfaces. */}
      <Route path={PAIR_PATH} element={<PairRoute />} />
      {ROUTES.map((route) => {
        const Container = LIVE_CONTAINERS[route.path];
        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              Container ? (
                <RouteErrorBoundary surface={route.surface}>
                  <Container />
                </RouteErrorBoundary>
              ) : (
                <Surface surface={route.surface} name={route.name} element={route.element} />
              )
            }
          />
        );
      })}
      {ROUTE_ALIASES.map((alias) => (
        <Route key={alias.from} path={alias.from} element={<Navigate to={alias.to} replace />} />
      ))}
      {UNBUILT_ROUTES.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <RouteNotBuilt
              path={route.path}
              label={route.label}
              reason={route.reason}
              ownedBy={route.ownedBy}
            />
          }
        />
      ))}
      <Route path="*" element={<UnknownRoute />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeController>
      {/* The router sits ABOVE the actor, deliberately (#26 / R6.1).

          `FeedbackProvider` keys its subtree on `sessionKey` (ActorContext.jsx). The first answer,
          or an actor published before it, keeps the tree that rendered while nobody was known
          (d-20261001-04). Later identity changes remount every route below it, even while the check
          is pending. A login-page sign-in can therefore keep its key if it beats the first check
          and nobody was already known; after that, a changed identity remounts the subtree. With
          the router inside that subtree, a navigation issued in the same update is lost.

          WHAT PROTECTS THIS LINE: nothing automated, and saying so is the point. Two earlier
          versions of this comment each named a test that does not in fact fail when the nesting is
          inverted, and both claims were disproved by inverting it on purpose:
            · `test/session-survives-signin.test.jsx` passes either way, because a `HashRouter`
              re-reads `window.location.hash` when it remounts and `navigate` had already written
              it. It recovers by luck, not by design.
            · `test/login-route.test.jsx` fails when ITS OWN tree is inverted, but it builds that
              tree in `renderLogin()` and never renders `App`, so it cannot see this file.
          The ordering is still right — recovering only because the URL happens to be global state
          is not a design — but it rests on the reasoning above, not on a gate. Change it and the
          suite will not stop you.

          The running application asks the server who is acting. A component test that renders
          ActorProvider directly supplies no resolver and therefore touches no network. */}
      <HashRouter>
        {/* `themeStore` is what turns the remembered theme from a device preference into a
            profile one (#36 / R9.2). `ActorProvider` hands it to `ProfileThemeBinding`, which
            binds it to the profile the SERVER named — never to one the browser chose. Without it
            that binding gets `store === undefined` and every choice stays on this device. */}
        <ActorProvider
          resolveSession={fetchSession}
          themeStore={themeStore}
          // #577: the household's saved sound choices, read and written through the server. A
          // failed read keeps every sound off; only a parent's save is accepted.
          loadFeedbackPreferences={fetchSoundPreferences}
          onSaveFeedbackPreferences={saveSoundPreferencesFromProvider}
          audioAssets={AUDIO_ASSETS}
        >
          {/* #578 AD-05 — the ONE parent re-check prompt. It registers with the API client, so
              every parent change in every screen that the server refuses for an old parent proof
              asks the parent once, then sends the same change again. */}
          {/* #582 AD-05 — a child's screen goes back to the picker at the server's deadline. */}
          <ChildActorReturn />
          <ParentRecheckProvider>
            {/* #580 — the tab's one household connection. Screens re-read through it; the kiosk
                keeps its own device connection and never gets this one. */}
            <HouseholdRealtimeProvider>
              <AppRoutes />
            </HouseholdRealtimeProvider>
          </ParentRecheckProvider>
        </ActorProvider>
      </HashRouter>
    </ThemeController>
  );
}
